import { ChevronDown as ExpandMore, ChevronLeft, ChevronRight } from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FormatPrice from "../../../../components/FormatPrice";
import { useCart } from "../../../../context/CartContext";
import { Accordion, AccordionDetails, AccordionSummary } from "../../../../components/ui/DataDisplay";
import { Snackbar } from "../../../../components/ui/Feedback";
import { Typography } from "../../../../components/ui/LayoutPrimitives";
import { addShoppingCartItems } from "../../../../api/action/user.jsx";
import { getShoppingCartItems } from "../../../../api/action/cart.jsx";

const ProdDetailsContext = createContext(null);
export const ProdDetailsProvider = ProdDetailsContext.Provider;
export const useProdDetails = () => useContext(ProdDetailsContext);

const getPlainText = (htmlValue) => {
    if (!htmlValue) return "No description available";

    if (typeof document === "undefined") {
        return htmlValue.replace(/<[^>]*>/g, "") || "No description available";
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlValue;
    return tempDiv.textContent || tempDiv.innerText || "No description available";
};

const AvikarProductDetails = () => {
    const navigate = useNavigate();
    const userDetail = useSelector((state) => state?.auth?.data);
    const storedProduct = useSelector(
        (state) => state.selectedProduct.selectedProduct
    );

    const [productDetails, setProductDetails] = useState("");
    const [quantity] = useState(1);
    const { addToCart } = useCart();

    const [alertMsg, setAlertMsg] = useState({
        open: false,
        message: "",
        severity: "error",
    });

    const handleClose = (event, reason) => {
        if (reason === "clickaway") return;
        setAlertMsg({ open: false });
    };

    useEffect(() => {
        setProductDetails(storedProduct);
        return () => setProductDetails([]);
    }, [storedProduct]);

    const addToShoppingCart = async (retailReq) => {
        const shoppingCartId = userDetail.account.shoppingCart.primaryId;
        if (!shoppingCartId) {
            setAlertMsg({
                message: "Shopping cart ID is not available. Please refresh the page.",
                open: true,
                severity: "error",
            });
            return;
        }

        try {
            const latestCart = await getShoppingCartItems(shoppingCartId);
            let existingProducts = latestCart[0]?.productList || [];

            let updatedProductList = existingProducts.some(
                (item) => item.product.primaryId === retailReq.product.primaryId
            )
                ? existingProducts.map((item) =>
                    item.product.primaryId === retailReq.product.primaryId
                        ? { ...item, quantity: item.quantity + retailReq.quantity }
                        : item
                )
                : [...existingProducts, retailReq];

            const payload = {
                primaryId: shoppingCartId,
                accountId: userDetail.account.primaryId,
                createdBy: "SOAP UI",
                modifiedBy: "SOAP UI",
                productList: updatedProductList,
                company: userDetail.account.company,
            };

            const response = await addShoppingCartItems(payload);
            if (response) {
                navigate("/cart");
            }
        } catch (error) {
            setAlertMsg({
                message: error.message,
                open: true,
                severity: "error",
            });
        }
    };

    const handleCart = async (productDetails) => {
        // if (!userDetail?.isAuthenticated) {
        //     navigate("/sign-in");
        //     return;
        // }

        let retailReq = {
            quantity: quantity,
            product: {
                primaryId: productDetails.primaryId,
                price: productDetails.price,
                bulkOrderEnabled: Boolean(productDetails.bulkOrderEnabled),
            },
        };

        await addToShoppingCart(retailReq);
        addToCart(retailReq);

        setAlertMsg({
            message: "Product added/updated in cart successfully",
            open: true,
            severity: "success",
        });
    };

    const [selectedValues, setSelectedValues] = useState({});
    const [mainImage, setMainImage] = useState("");

    useEffect(() => {
        if (
            productDetails?.imageContents &&
            productDetails.imageContents.length > 0
        ) {
            setMainImage(productDetails.imageContents[0].displayLink);
        }
    }, [productDetails]);

    return (
        <>
            <div className="sp mx-auto max-w-7xl px-2 py-10 lg:px-0">
                <div className="overflow-hidden">
                    <div className="mb-9 pt-4 md:px-6 md:pt-7 lg:mb-2 lg:p-8 2xl:p-10 2xl:pt-10">
                        {/* Thumbnail images */}
                        <div className="flex flex-wrap justify-start mb-4 overflow-x-auto max-w-full">
                            {productDetails?.imageContents &&
                                productDetails.imageContents.map((imgObj, index) => (
                                    <img
                                        key={index}
                                        src={imgObj.displayLink}
                                        alt={`${productDetails?.name || "Product"} thumbnail ${index + 1}`}
                                        className="h-16 w-16 object-contain rounded-md m-1 border border-gray-300 shadow-sm cursor-pointer bg-white"
                                        onMouseEnter={() => setMainImage(imgObj.displayLink)}
                                    />
                                ))}
                        </div>

                        <div className="items-start justify-between lg:flex lg:space-x-8">
                            {/* Main Image Section */}
                            <div className="mb-4 items-center justify-center overflow-hidden md:mb-8 lg:mb-0 xl:flex">
                                <div className="w-full xl:flex xl:flex-row-reverse">
                                    <div className="relative mb-2.5 w-full shrink-0 overflow-hidden rounded-md border md:mb-3 xl:w-[480px] 2xl:w-[650px] lg:h-[468px] h-[300px]">
                                        <div className="relative flex items-center justify-center h-full">
                                            <img
                                                loading="lazy"
                                                src={mainImage}
                                                alt={productDetails?.name}
                                                width={650}
                                                height={590}
                                                className="rounded-lg object-contain md:h-[300px] md:w-full scale-75 lg:h-full"
                                            />
                                        </div>
                                        <div className="absolute top-2/4 z-10 flex w-full items-center justify-between">
                                            <ChevronLeft className="text-white" />
                                            <ChevronRight className="text-white" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Product Info Section */}
                            <div className="flex shrink-0 flex-col lg:w-[430px] xl:w-[470px] 2xl:w-[480px] capitalize">
                                <div className="pb-2">
                                    <h2 className="text-lg text-center font-semibold md:text-xl xl:text-2xl text-black">
                                        {productDetails?.name}
                                    </h2>
                                    <p className="mt-4 text-center font-semibold text-xl">
                                        {productDetails?.price && (
                                            <FormatPrice
                                                code={productDetails.price.currency || "USD"}
                                                price={productDetails.price.priceAmt}
                                            />
                                        )}
                                    </p>
                                </div>

                                <div className="space-s-4 3xl:pr-48 flex items-center gap-6 border-b border-gray-300 py-8 md:pr-32 lg:pr-12 2xl:pr-12">
                                    <button
                                        onClick={() => handleCart(productDetails)}
                                        className={`mt-2 py-2 px-4 rounded-full w-full text-white bg-[#f4a649] 
  hover:bg-white hover:text-[#f4a649] border border-[#f4a649] 
  transition duration-300 text-sm md:text-base flex items-center justify-center`}
                                    >
                                        Add to cart
                                    </button>
                                </div>

                                {/* Description */}
                                <div className="pt-6 xl:pt-8">
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <h3 className="text-15px mb-3 font-semibold sm:text-base lg:mb-3.5">
                                                Description:
                                            </h3>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography>{getPlainText(productDetails?.description)}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                </div>

                                {/* Product Details */}
                                <div className="pt-6 xl:pt-8">
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <h3 className="text-15px mb-3 font-semibold sm:text-base lg:mb-3.5">
                                                Details about product
                                            </h3>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography
                                                component="div"
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        productDetails?.packageDescription ||
                                                        "<p>No description available</p>",
                                                }}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                </div>

                                {/* Optional Attributes */}
                                {productDetails.optionalAttributes?.length > 0 && (
                                    <div className="pt-6 xl:pt-8">
                                        <h3 className="text-15px mb-3 font-semibold sm:text-base lg:mb-3.5">
                                            Additional Informations:
                                        </h3>
                                        {productDetails.optionalAttributes.map((attribute, index) => (
                                            <div key={index} className="mb-4">
                                                <h4 className="font-semibold">
                                                    {attribute.name}:
                                                    <span
                                                        style={{
                                                            fontWeight: "normal",
                                                            display: "flex",
                                                            gap: "8px",
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        {attribute.values.map((value, idx) => {
                                                            const imgUrl = value.imgContent?.displayLink || "";
                                                            return (
                                                                <React.Fragment key={idx}>
                                                                    {imgUrl ? (
                                                                        <img
                                                                            src={imgUrl}
                                                                            alt={value.value}
                                                                            loading="lazy"
                                                                            className={`w-12 h-12 border rounded-full cursor-pointer transition-all ${selectedValues[attribute.name] === value.value
                                                                                ? "border-[#f4a649] ring-2 ring-[#f4a649]"
                                                                                : "border-gray-300"
                                                                                }`}
                                                                            title={value.value}
                                                                            onClick={() => {
                                                                                setSelectedValues({
                                                                                    ...selectedValues,
                                                                                    [attribute.name]: value.value,
                                                                                });

                                                                                // Update main image to selected variant
                                                                                if (imgUrl) {
                                                                                    setMainImage(imgUrl);
                                                                                }
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <span>{value?.value}</span>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </span>
                                                </h4>
                                            </div>
                                        ))}
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>

                {/* Snackbar for Alerts */}
                <Snackbar
                    autoHideDuration={6000}
                    isOpen={alertMsg.open}
                    message={alertMsg.message}
                    onClose={handleClose}
                    placement="top"
                    tone={alertMsg.severity}
                />
            </div>
        </>
    );
};

export default AvikarProductDetails;
